/**
 * 集装箱类型管理前端接口
 * @author ChaseZhang
 * @since 2025-01-27
 */

import { httpClient as request } from '@/utils/http'

// ==================== 类型定义 ====================

/**
 * 集装箱类型响应模型
 */
export interface ContainerTypeModel {
  /** 唯一标识 */
  id: string;
  /** 租户ID */
  tenantId?: string;
  /** 箱型代码，业务唯一标识 */
  containerTypeCode: string;
  /** ISO代码 */
  isoCode?: string;
  /** 描述 */
  description?: string;
  /** 启用状态: 1-启用, 0-禁用 */
  enabled: number;
  /** 是否为平台数据 */
  isPlatformData?: boolean;
  /** EDI代码列表 */
  ediCodes?: CommonEdiCodeModel[];
}

/**
 * 通用EDI代码模型
 */
export interface CommonEdiCodeModel {
  /** EDI代码ID */
  id: string;
  /** 租户ID */
  tenantId?: string;
  /** 平台名称 */
  platform: string;
  /** EDI代码 */
  ediCode: string;
  /** EDI类型: 1-承运人, 2-包装单位, 3-集装箱类型 */
  ediType: number;
  /** 业务实体代码 */
  businessEntityCode?: string;
  /** 业务实体ID */
  businessEntityId?: string;
  /** 承运人ID（可选） */
  carrierId?: string;
  /** 启用状态 */
  enabled: number;
  /** 创建时间 */
  dateCreated: string;
  /** 修改时间 */
  dateModified: string;
}

/**
 * 集装箱类型查询请求模型
 */
export interface ContainerTypeQueryRequest {
  /** 当前页码，默认为1 */
  pageNum?: number;
  /** 每页大小，默认为10 */
  pageSize?: number;
  /** 关键词，用于模糊查询代码、ISO代码、描述 */
  keyword?: string;
  /** 箱型代码，支持模糊查询 */
  containerTypeCode?: string;
  /** ISO代码，支持模糊查询 */
  isoCode?: string;
  /** 描述，支持模糊查询 */
  description?: string;
  /** 启用状态: 1-启用, 0-禁用 */
  enabled?: number;
  /** 租户ID */
  tenantId?: string;
}

/**
 * 集装箱类型保存请求模型
 */
export interface ContainerTypeSaveRequest {
  /** 主键ID，更新时传递 */
  id?: string;
  /** 箱型代码，业务唯一标识 */
  containerTypeCode: string;
  /** ISO代码 */
  isoCode?: string;
  /** 描述 */
  description?: string;
  /** 启用状态: 1-启用, 0-禁用，默认为1 */
  enabled?: number;
  /** 租户ID */
  tenantId?: string;
}

/**
 * 模糊搜索请求参数
 */
export interface FuzzySearchRequest {
  /** 当前页码，默认为1 */
  pageNum?: number;
  /** 每页大小，默认为10 */
  pageSize?: number;
  /** 搜索关键字，支持模糊匹配名称和代码 */
  keyword?: string;
  /** 启用状态: 1-启用, 0-禁用 */
  enabled?: number;
  /** 租户ID */
  tenantId?: string;
}

/**
 * EDI代码信息
 */
export interface EdiCodeInfo {
  /** 平台名称 */
  platform: string;
  /** 承运人ID（可选） */
  carrier?: string;
  /** EDI代码 */
  ediCode: string;
}

/**
 * 集装箱类型EDI保存请求
 */
export interface ContainerTypeEdiSaveRequest {
  /** 箱型代码 */
  containerTypeCode: string;
  /** 平台名称 */
  platform: string;
  /** EDI代码 */
  ediCode: string;
}

/**
 * 批量集装箱类型EDI保存请求
 */
export interface BatchContainerTypeEdiSaveRequest {
  /** 箱型代码 */
  containerTypeCode: string;
  /** EDI代码列表 */
  ediCodes: EdiCodeInfo[];
}

/**
 * 集装箱类型EDI查询请求
 */
export interface ContainerTypeEdiQueryRequest {
  /** 箱型代码 */
  containerTypeCode: string;
}

/**
 * 集装箱类型EDI检查请求
 */
export interface ContainerTypeEdiCheckRequest {
  /** 平台名称 */
  platform: string;
  /** EDI代码 */
  ediCode: string;
}

/**
 * 分页响应模型
 */
export interface PageResponse<T> {
  /** 当前页码 */
  current: number;
  /** 每页大小 */
  size: number;
  /** 总记录数 */
  total: number;
  /** 总页数 */
  pages: number;
  /** 数据列表 */
  records: T[];
}

/**
 * 启用状态枚举
 */
export enum EnabledStatus {
  /** 禁用 */
  DISABLED = 0,
  /** 启用 */
  ENABLED = 1
}

// ==================== API接口 ====================

/**
 * 集装箱类型管理API类
 */
export class ContainerTypeApi {
  private static readonly BASE_URL = '/mdm/containerType'

  // ==================== 核心CRUD接口 ====================

  /**
   * 分页查询集装箱类型列表
   */
  static async queryPage(params: ContainerTypeQueryRequest): Promise<PageResponse<ContainerTypeModel>> {
    return request.post(`${ContainerTypeApi.BASE_URL}/queryPage`, params)
  }

  /**
   * 查询集装箱类型列表（不分页）
   */
  static async queryList(params: ContainerTypeQueryRequest): Promise<ContainerTypeModel[]> {
    return request.post(`${ContainerTypeApi.BASE_URL}/queryList`, params)
  }

  /**
   * 模糊搜索集装箱类型
   */
  static async search(params: FuzzySearchRequest): Promise<ContainerTypeModel[]> {
    return request.post(`${ContainerTypeApi.BASE_URL}/search`, params)
  }

  /**
   * 根据ID获取集装箱类型详情
   */
  static async getById(id: string): Promise<ContainerTypeModel> {
    return request.post(`${ContainerTypeApi.BASE_URL}/getById`, { id })
  }

  /**
   * 保存集装箱类型（新增或更新）
   */
  static async save(params: ContainerTypeSaveRequest): Promise<boolean> {
    return request.post(`${ContainerTypeApi.BASE_URL}/save`, params)
  }

  /**
   * 删除集装箱类型
   */
  static async delete(id: string): Promise<boolean> {
    return request.post(`${ContainerTypeApi.BASE_URL}/delete`, { id })
  }

  // ==================== 批量操作接口 ====================

  /**
   * 批量保存集装箱类型
   */
  static async batchSave(containerTypes: ContainerTypeSaveRequest[]): Promise<boolean> {
    return request.post(`${ContainerTypeApi.BASE_URL}/batchSave`, { containerTypes })
  }

  /**
   * 批量删除集装箱类型
   */
  static async batchDelete(ids: string[]): Promise<boolean> {
    return request.post(`${ContainerTypeApi.BASE_URL}/batchDelete`, { ids })
  }

  /**
   * 更新集装箱类型状态
   */
  static async updateStatus(ids: string[], enabled: number): Promise<boolean> {
    return request.post(`${ContainerTypeApi.BASE_URL}/updateStatus`, {
      ids,
      enabled
    })
  }

  /**
   * 批量更新集装箱类型状态
   */
  static async batchUpdateStatus(ids: string[], enabled: number): Promise<boolean> {
    return request.post(`${ContainerTypeApi.BASE_URL}/batchUpdateStatus`, {
      ids,
      enabled
    })
  }

  // ==================== 专用查询接口 ====================

  /**
   * 根据箱型代码查询集装箱类型
   */
  static async getByCode(code: string): Promise<ContainerTypeModel> {
    return request.post(`${ContainerTypeApi.BASE_URL}/getByCode`, { code })
  }

  /**
   * 根据ISO代码查询集装箱类型列表
   */
  static async queryByIsoCode(isoCode: string): Promise<ContainerTypeModel[]> {
    return request.post(`${ContainerTypeApi.BASE_URL}/queryByIsoCode`, { isoCode })
  }

  /**
   * 查询启用状态的集装箱类型列表
   */
  static async queryEnabled(): Promise<ContainerTypeModel[]> {
    return request.post(`${ContainerTypeApi.BASE_URL}/queryEnabled`, {})
  }

  // ==================== 租户与平台关联接口 ====================

  /**
   * 分页查询可关联的平台集装箱类型
   */
  static async queryPlatformPage(params: ContainerTypeQueryRequest): Promise<PageResponse<ContainerTypeModel>> {
    return request.post(`${ContainerTypeApi.BASE_URL}/queryPlatformPage`, params)
  }

  /**
   * 查询可关联的平台集装箱类型列表
   */
  static async queryPlatformList(params: ContainerTypeQueryRequest): Promise<ContainerTypeModel[]> {
    return request.post(`${ContainerTypeApi.BASE_URL}/queryPlatformList`, params)
  }

  /**
   * 批量关联平台集装箱类型到租户
   */
  static async batchAssociate(ids: string[]): Promise<boolean> {
    return request.post(`${ContainerTypeApi.BASE_URL}/batchAssociate`, { ids })
  }

  /**
   * 批量取消关联
   */
  static async batchDisassociate(ids: string[]): Promise<boolean> {
    return request.post(`${ContainerTypeApi.BASE_URL}/batchDisassociate`, { ids })
  }

  // ==================== EDI代码管理接口 ====================

  /**
   * 查询集装箱类型的EDI代码列表
   */
  static async queryEdiCodes(params: ContainerTypeEdiQueryRequest): Promise<CommonEdiCodeModel[]> {
    return request.post(`${ContainerTypeApi.BASE_URL}/edi/queryList`, params)
  }

  /**
   * 保存集装箱类型EDI代码
   */
  static async saveEdiCode(params: ContainerTypeEdiSaveRequest): Promise<boolean> {
    return request.post(`${ContainerTypeApi.BASE_URL}/edi/save`, params)
  }

  /**
   * 批量保存集装箱类型EDI代码
   */
  static async batchSaveEdiCodes(params: BatchContainerTypeEdiSaveRequest): Promise<boolean> {
    return request.post(`${ContainerTypeApi.BASE_URL}/edi/batchSave`, params)
  }

  /**
   * 删除集装箱类型EDI代码
   */
  static async deleteEdiCode(ediCodeId: string): Promise<boolean> {
    return request.post(`${ContainerTypeApi.BASE_URL}/edi/delete`, { id: ediCodeId })
  }

  /**
   * 批量删除集装箱类型EDI代码
   */
  static async batchDeleteEdiCodes(ediCodeIds: string[]): Promise<boolean> {
    return request.post(`${ContainerTypeApi.BASE_URL}/edi/batchDelete`, {
      ids: ediCodeIds
    })
  }

  /**
   * 检查集装箱类型EDI代码是否存在
   */
  static async checkEdiCodeExists(params: ContainerTypeEdiCheckRequest): Promise<boolean> {
    return request.post(`${ContainerTypeApi.BASE_URL}/edi/checkExists`, params)
  }
}

// ==================== 便捷服务 ====================

/**
 * 集装箱类型管理便捷服务
 */
export const ContainerTypeService = {
  // 核心CRUD
  queryPage: ContainerTypeApi.queryPage,
  queryList: ContainerTypeApi.queryList,
  search: ContainerTypeApi.search,
  getById: ContainerTypeApi.getById,
  save: ContainerTypeApi.save,
  delete: ContainerTypeApi.delete,

  // 批量操作
  batchSave: ContainerTypeApi.batchSave,
  batchDelete: ContainerTypeApi.batchDelete,
  updateStatus: ContainerTypeApi.updateStatus,
  batchUpdateStatus: ContainerTypeApi.batchUpdateStatus,

  // 专用查询
  getByCode: ContainerTypeApi.getByCode,
  queryByIsoCode: ContainerTypeApi.queryByIsoCode,
  queryEnabled: ContainerTypeApi.queryEnabled,

  // 租户关联
  queryPlatformPage: ContainerTypeApi.queryPlatformPage,
  queryPlatformList: ContainerTypeApi.queryPlatformList,
  batchAssociate: ContainerTypeApi.batchAssociate,
  batchDisassociate: ContainerTypeApi.batchDisassociate,

  // EDI代码管理
  queryEdiCodes: ContainerTypeApi.queryEdiCodes,
  saveEdiCode: ContainerTypeApi.saveEdiCode,
  batchSaveEdiCodes: ContainerTypeApi.batchSaveEdiCodes,
  deleteEdiCode: ContainerTypeApi.deleteEdiCode,
  batchDeleteEdiCodes: ContainerTypeApi.batchDeleteEdiCodes,
  checkEdiCodeExists: ContainerTypeApi.checkEdiCodeExists
}

export default ContainerTypeApi
